import { SocialLogin } from '@capgo/capacitor-social-login';
import { v4 as uuidv4 } from 'uuid';

import authMethods from 'constants/authMethods';
import { AZURE_PROVIDER_ID, buildInitializeOptions, type NativeOAuthProvider } from 'services/auth/oauthConfig';

/**
 * Native Microsoft/Google SSO via `@capgo/capacitor-social-login`, returns the provider token to `auth-get-user-access`.
 * Both providers yield an OIDC `id_token`; the backend verifies it (Microsoft + native Google both carry a `nonce` the backend checks).
 */

export interface NativeAuthResult {
  authMethod: string;
  token: string;
  authNonce: string;
}

/**
 * Thrown when the user dismisses the sign-in flow. Callers treat this as a no-op, not an error.
 */
export class NativeAuthCancelledError extends Error {
  constructor() {
    super('Native authentication was cancelled');
    this.name = 'NativeAuthCancelledError';
  }
}

/**
 * capacitor-social-login marks user-dismissals with `error.code === 'USER_CANCELLED'`.
 */
const isUserCancelled = (error: unknown): boolean =>
  typeof error === 'object' && error !== null && (error as { code?: string }).code === 'USER_CANCELLED';

/**
 * The plugin must be initialized before the first login, and exactly once.
 */
let initPromise: Promise<void> | null = null;
const ensureInitialized = (): Promise<void> => {
  if (!initPromise) {
    initPromise = SocialLogin.initialize(buildInitializeOptions());
  }
  return initPromise;
};

/**
 * Clears the memoized plugin init so the next login re-initializes with the current tenant's OAuth ids.
 * Called when switching workspace, since the selected instance may use different OAuth credentials.
 */
export const resetNativeOAuth = (): void => {
  initPromise = null;
};

const SIGNED_IN_PROVIDER_KEY = 'a1.nativeAuthProvider';

const rememberProvider = (provider: NativeOAuthProvider): void => {
  try {
    localStorage.setItem(SIGNED_IN_PROVIDER_KEY, provider);
  } catch {
    // Storage unavailable.
  }
};

const readRememberedProvider = (): NativeOAuthProvider | null => {
  try {
    const provider = localStorage.getItem(SIGNED_IN_PROVIDER_KEY);
    return provider === authMethods.Microsoft || provider === authMethods.Google ? (provider as NativeOAuthProvider) : null;
  } catch {
    return null;
  }
};

const forgetProvider = (): void => {
  try {
    localStorage.removeItem(SIGNED_IN_PROVIDER_KEY);
  } catch {
    // Storage unavailable.
  }
};

/** Ends the cached provider session for whichever provider signed the current session in. */
export const signOutNativeProvider = async (): Promise<void> => {
  const provider = readRememberedProvider();
  if (!provider) return;

  try {
    await ensureInitialized();
    await SocialLogin.logout(
      provider === authMethods.Microsoft ? { provider: 'oauth2', providerId: AZURE_PROVIDER_ID } : { provider: 'google' }
    );
    forgetProvider();
  } catch (error) {
    console.error(`Unable to end the ${provider} provider session:`, error);
  }
};

const authenticateMicrosoft = async (nonce: string): Promise<string> => {
  const { result } = await SocialLogin.login({
    provider: 'oauth2',
    options: { providerId: AZURE_PROVIDER_ID, additionalParameters: { nonce } }
  });

  const token = 'idToken' in result ? result.idToken : null;
  if (!token) throw new Error('Microsoft authentication did not return an id_token');
  return token;
};

const authenticateGoogle = async (nonce: string): Promise<string> => {
  // The default scopes put the email into the returned id_token, which is all the backend needs.
  const { result } = await SocialLogin.login({
    provider: 'google',
    options: { nonce, forcePrompt: true }
  });

  const token = 'idToken' in result ? result.idToken : null;
  if (!token) throw new Error('Google authentication did not return an id_token');
  return token;
};

export const authenticateNative = async (provider: NativeOAuthProvider): Promise<NativeAuthResult> => {
  await ensureInitialized();
  const nonce = uuidv4();

  try {
    if (provider === authMethods.Microsoft) {
      const token = await authenticateMicrosoft(nonce);
      rememberProvider(provider);
      return { authMethod: authMethods.Microsoft, token, authNonce: nonce };
    }

    const token = await authenticateGoogle(nonce);
    rememberProvider(provider);
    return { authMethod: authMethods.Google, token, authNonce: nonce };
  } catch (error) {
    if (isUserCancelled(error)) {
      throw new NativeAuthCancelledError();
    }
    throw error;
  }
};
